import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { node } from 'prop-types';
import { useLocation } from 'react-router-dom';

import { errorLogger } from '@ajaibid/utils';
import { API_SUCCESS_CODE, DEFAULT_GET_RESPONSE } from '@config/constanta';
import { AuthContext } from '@context/auth';
import featureToggle, { MARGIN_TRADING } from '@config/featureToggle';

import { getMtTradelimit } from '@services/api/stock/marginTrading/account';
import { getMtPortfolioList } from '@services/api/stock/marginTrading/portfolio';
import { postMtBuy, postMtSell } from '@services/api/stock/marginTrading/trading';

import {
  getMTEligibilityStatus,
  getMarginOnboarding,
  getMarginStatus,
  getMarginStockAvailable,
} from '@screens/MarginTrading/fetcher';
import { useQueryParams } from '@hooks/useQueryParams';

export const MarginTradingContext = React.createContext();

const MarginTradingProvider = ({ children }) => {
  const { queryParams } = useQueryParams();
  const featMarginTrading = featureToggle.some(
    (feat) => feat.feature === MARGIN_TRADING && feat.toggle
  );
  const { mtActive } = useContext(AuthContext);
  const [mtPorto, setMTPorto] = useState(DEFAULT_GET_RESPONSE);
  const [loadingPorto, setLoadingPorto] = useState(true);
  const location = useLocation();
  const includeMtPorto =
    queryParams.get('tab') === 'marginTrading' || location.pathname.includes('/saham/');
  const isFetchStatus = location.pathname.toLowerCase().includes('/margin-trading');

  const fetchMarginPortoList = useCallback(() => {
    // requested only if feature toggle true
    if (featMarginTrading) {
      setLoadingPorto(true);
      getMtPortfolioList()
        .then((response) => {
          setMTPorto(response);
          setLoadingPorto(false);
        })
        .catch((err) => {
          console.error(err);
          setLoadingPorto(false);
        });
    }
  }, [featMarginTrading, getMtPortfolioList]);

  const [mtTradeLimit, setMtTradeLimit] = useState({});
  const [loadingTrade, setLoadingMarginTrade] = useState(true);

  const fetchMarginTradeLimit = useCallback(
    (code) => {
      // requested only if feature toggle true
      if (featMarginTrading && mtActive) {
        setLoadingMarginTrade(true);
        getMtTradelimit(code)
          .then((response) => {
            setMtTradeLimit(response);
            setLoadingMarginTrade(false);
          })
          .catch((err) => {
            console.error(err);
            setLoadingMarginTrade(false);
          });
      }
    },
    [featMarginTrading, getMtTradelimit]
  );

  const [mtStockAvailable, setMTStockAvailable] = useState(false);
  const [loadingStockAvailable, setLoadingStockAvailable] = useState(true);

  const fetchMarginStockAvailable = useCallback(
    (code) => {
      // requested only if feature toggle true
      if (featMarginTrading && mtActive) {
        setLoadingStockAvailable(true);
        getMarginStockAvailable(code)
          .then((response) => {
            const active = response?.status_margin === 'ACTIVE';
            setMTStockAvailable(active);
            setLoadingStockAvailable(false);
          })
          .catch((err) => {
            console.error(err);
            setLoadingStockAvailable(false);
          });
      }
    },
    [featMarginTrading, getMarginStockAvailable]
  );

  const [eligibleStatus, setEligibleStatus] = useState(false);
  const [loadingEligible, setLoadingEligible] = useState(true);

  const fetchEligibleStatus = useCallback(() => {
    // requested only if feature toggle true
    if (featMarginTrading) {
      setLoadingEligible(true);
      getMTEligibilityStatus()
        .then((response) => {
          setEligibleStatus(response?.result?.is_mt_eligible);
          setLoadingEligible(false);
        })
        .catch((err) => {
          console.error(err);
          setEligibleStatus(false);
          setLoadingEligible(false);
        });
    }
  }, [featMarginTrading, getMTEligibilityStatus]);

  const [mtOnboarding, setOnboarding] = useState([]);
  const [loadingOnboarding, setLoadingOnboarding] = useState(true);
  const [onboardingMenu, setOnboardingMenu] = useState([]);

  const fetchOneOnboardDetail = useCallback(
    async (id = 0) => {
      if (featMarginTrading) {
        try {
          const resp = await getMarginOnboarding(id);
          if (resp?.err_code === API_SUCCESS_CODE) {
            setOnboarding(resp?.results);
          }
        } catch (err) {
          errorLogger('ERROR_API', err.message);
        }
      }
    },
    [featMarginTrading, getMarginOnboarding]
  );

  const fetchMarginOnboarding = useCallback(async () => {
    // requested only if feature toggle true
    if (featMarginTrading) {
      try {
        setLoadingOnboarding(true);
        const resp = await getMarginOnboarding(0);
        if (resp?.err_code === API_SUCCESS_CODE) {
          setOnboardingMenu(resp?.results);
          const firstId = resp?.results?.[0]?.id || 1;
          await fetchOneOnboardDetail(firstId);
        }
      } catch (err) {
        errorLogger('ERROR_API', err.message);
      } finally {
        setLoadingOnboarding(false);
      }
    }
  }, [featMarginTrading, getMarginOnboarding]);

  const [marginStatus, setMarginStatus] = useState({});
  const [loadingMarginStatus, setLoadingMarginStatus] = useState(true);

  const fetchMarginStatus = useCallback(() => {
    // requested only if feature toggle true
    if (featMarginTrading) {
      setLoadingMarginStatus(true);
      getMarginStatus()
        .then((response) => {
          setMarginStatus(response?.result || {});
          setLoadingMarginStatus(false);
        })
        .catch((err) => {
          console.error(err);
          setLoadingMarginStatus(false);
        });
    }
  }, [featMarginTrading, getMarginStatus]);

  useEffect(() => {
    let intervalPortoMt;
    // requested only if feature toggle true
    if (featMarginTrading && mtActive) {
      fetchMarginPortoList();
    }
    if (featMarginTrading && includeMtPorto && mtActive) {
      intervalPortoMt = setInterval(() => {
        fetchMarginPortoList();
      }, 5000);
    }
    return () => clearInterval(intervalPortoMt);
  }, [featMarginTrading, includeMtPorto, mtActive]);

  useEffect(() => {
    if (featMarginTrading && isFetchStatus) {
      fetchEligibleStatus();
    }
  }, [featMarginTrading, isFetchStatus]);

  const onBuyMarginStock = useCallback((payload) => postMtBuy(payload), [postMtBuy]);
  const onSellMarginStock = useCallback((payload) => postMtSell(payload), [postMtSell]);

  const valueProps = useMemo(
    () => ({
      mtActive,
      eligibleStatus,
      loadingEligible,
      loadingMarginStatus,
      loadingOnboarding,
      loadingPorto,
      loadingStockAvailable,
      loadingTrade,
      marginStatus,
      mtOnboarding,
      onboardingMenu,
      mtPorto,
      mtStockAvailable,
      mtTradeLimit,
      fetchEligibleStatus,
      fetchMarginPortoList,
      fetchMarginStatus,
      fetchMarginStockAvailable,
      fetchMarginTradeLimit,
      fetchMarginOnboarding,
      fetchOneOnboardDetail,
      onBuyMarginStock,
      onSellMarginStock,
    }),
    [
      mtActive,
      eligibleStatus,
      loadingEligible,
      loadingMarginStatus,
      loadingOnboarding,
      loadingPorto,
      loadingStockAvailable,
      loadingTrade,
      marginStatus,
      mtOnboarding,
      onboardingMenu,
      mtPorto,
      mtStockAvailable,
      mtTradeLimit,
      fetchEligibleStatus,
      fetchMarginPortoList,
      fetchMarginStatus,
      fetchMarginStockAvailable,
      fetchMarginTradeLimit,
      fetchMarginOnboarding,
      fetchOneOnboardDetail,
      onBuyMarginStock,
      onSellMarginStock,
    ]
  );

  return (
    <MarginTradingContext.Provider value={valueProps}>{children}</MarginTradingContext.Provider>
  );
};

MarginTradingProvider.propTypes = {
  children: node.isRequired,
};

export default MarginTradingProvider;
